import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import {
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@cotera/client/v0/components/ui/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@cotera/client/v0/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@cotera/client/v0/components/ui/dropdown-menu';
import { Tooltip } from '@cotera/client/app/components/ui/tooltip';
import { Icon } from '@cotera/client/app/components';
import { NavItem, SideBarSection } from './types';
import { cn } from '../../lib/utils';

type LinkNavItemProps = {
  item: Extract<NavItem, { t: 'link' }>;
};

type SimpleItemNavItemProps = {
  item: Extract<NavItem, { t: 'item' }>;
};

type CollapsibleItemNavItemProps = {
  item: Extract<NavItem, { t: 'item' }> & { items: NavItem[] };
};

// Custom hook to scroll active items into view
const useScrollActiveItem = (isActive: boolean | undefined) => {
  const ref = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (isActive && ref.current) {
      // Small delay to ensure the parent has expanded
      const timer = setTimeout(() => {
        ref.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }, 100);

      return () => clearTimeout(timer);
    }

    return () => {};
  }, [isActive]);

  return ref;
};

// Helper function to check if any child item is active
const hasActiveChild = (item: NavItem): boolean => {
  if (item.t === 'item' && item.items && item.items.length > 0) {
    return item.items.some((child) => {
      if (child.isActive?.()) {
        return true;
      }
      return hasActiveChild(child);
    });
  }
  return false;
};

// Type guards
const isLinkItem = (item: NavItem): item is Extract<NavItem, { t: 'link' }> => {
  return item.t === 'link';
};

const isItemWithChildren = (
  item: NavItem
): item is Extract<NavItem, { t: 'item' }> & { items: NavItem[] } => {
  return (
    item.t === 'item' && Array.isArray(item.items) && item.items.length > 0
  );
};

const isSimpleItem = (
  item: NavItem
): item is Extract<NavItem, { t: 'item' }> => {
  return item.t === 'item' && (!item.items || item.items.length === 0);
};

// Helper component for rendering quick actions
const QuickActions = ({
  quickActions,
  groupClass = 'group-hover/menu-item:opacity-100',
  positioning = 'default',
}: {
  quickActions: NonNullable<NavItem['quickActions']>;
  groupClass?: string;
  positioning?: 'default' | 'none';
}) => {
  if (quickActions === undefined || quickActions.length === 0) {
    return null;
  }

  // Separate regular actions from grouped actions
  const regularActions = quickActions.filter(
    (action) => action.variant !== 'grouped'
  );
  const groupedActions = quickActions.filter(
    (action) => action.variant === 'grouped'
  );

  if (quickActions.length === 1 && groupedActions.length === 0) {
    return (
      <Tooltip side="top" tooltipContent={quickActions[0]!.label}>
        <SidebarGroupAction asChild title={quickActions[0]!.label}>
          <button
            className={cn(
              positioning === 'default' && '!top-1.5',
              'flex items-center justify-center w-5 h-5 cursor-pointer transition-opacity text-sidebar-foreground/55 hover:text-sidebar-foreground focus-visible:opacity-100',
              'opacity-100 sm:opacity-0',
              groupClass
            )}
            onClick={quickActions[0]!.onClick}
          >
            <Icon icon={quickActions[0]!.icon} />
          </button>
        </SidebarGroupAction>
      </Tooltip>
    );
  }

  return (
    <div
      className={`flex items-center gap-0.5 absolute ${
        positioning === 'default' ? 'top-1.5 right-1.5' : 'top-3.5 right-3'
      }`}
    >
      {/* Regular action buttons */}
      {regularActions.map((action, index) => (
        <Tooltip key={index} side="top" tooltipContent={action.label}>
          <SidebarGroupAction asChild title={action.label}>
            <button
              className={cn(
                positioning === 'default' && '!relative !top-0 !right-0',
                '!w-5 !h-5 cursor-pointer transition-opacity text-sidebar-foreground/55 hover:text-sidebar-foreground focus-visible:opacity-100',
                'opacity-100 sm:opacity-0',
                groupClass
              )}
              onClick={action.onClick}
            >
              <Icon icon={action.icon} />
            </button>
          </SidebarGroupAction>
        </Tooltip>
      ))}

      {/* Kebab menu for grouped actions */}
      {groupedActions.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarGroupAction asChild title="More actions">
              <button
                className={cn(
                  positioning === 'default' && '!relative !top-0 !right-0',
                  'flex items-center justify-center w-5 h-5 cursor-pointer transition-opacity text-sidebar-foreground/55 hover:text-sidebar-foreground focus-visible:opacity-100',
                  'opacity-100 sm:opacity-0',
                  groupClass
                )}
              >
                <Icon icon="ellipsis-vertical" />
              </button>
            </SidebarGroupAction>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="right" className="p-1">
            <DropdownMenuLabel>More actions</DropdownMenuLabel>
            {groupedActions.map((action, index) => (
              <DropdownMenuItem
                key={index}
                onClick={action.onClick}
                className="flex items-center gap-2"
              >
                <Icon icon={action.icon} />
                <span className="text-sm">{action.label}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};

export const LinkNavItem = ({ item }: LinkNavItemProps) => {
  const scrollRef = useScrollActiveItem(item.isActive?.());

  return (
    <SidebarMenuItem ref={scrollRef}>
      <SidebarMenuButton
        asChild
        tooltip={item.title}
        isActive={item.isActive?.()}
      >
        <Link to={item.url} target={item.target}>
          {item.icon && <Icon icon={item.icon} />}
          <span
            className={
              item.quickActions && item.quickActions.length > 0
                ? 'pr-10 sm:pr-0 sm:group-hover/menu-item:pr-10'
                : ''
            }
          >
            {item.title}
          </span>
        </Link>
      </SidebarMenuButton>
      {item.quickActions && (
        <QuickActions quickActions={item.quickActions} positioning="default" />
      )}
    </SidebarMenuItem>
  );
};

export const SimpleItemNavItem = ({ item }: SimpleItemNavItemProps) => {
  const scrollRef = useScrollActiveItem(item.isActive?.());

  return (
    <SidebarMenuItem ref={scrollRef}>
      <SidebarMenuButton
        asChild
        tooltip={item.title}
        isActive={item.isActive?.()}
      >
        <button onClick={item.onClick} className="cursor-pointer">
          {item.icon && <Icon icon={item.icon} />}
          <span
            className={
              item.quickActions && item.quickActions.length > 0
                ? 'pr-10 sm:pr-0 sm:group-hover/menu-item:pr-10'
                : ''
            }
          >
            {item.title}
          </span>
        </button>
      </SidebarMenuButton>
      {item.quickActions && (
        <QuickActions quickActions={item.quickActions} positioning="default" />
      )}
    </SidebarMenuItem>
  );
};

// Recursive component to render nav items with arbitrary depth
const RecursiveNavItem = ({
  item,
  depth = 0,
}: {
  item: NavItem;
  depth?: number;
}) => {
  // Call hooks at the top level, regardless of item type
  const scrollRef = useScrollActiveItem(item.isActive?.());

  if (isLinkItem(item)) {
    return (
      <SidebarMenuSubItem key={item.title} ref={scrollRef}>
        <SidebarMenuSubButton asChild isActive={item.isActive?.()} size="sm">
          <Link to={item.url}>
            {item.icon && <Icon icon={item.icon} />}
            <span
              className={
                item.quickActions && item.quickActions.length > 0
                  ? 'pr-10 sm:pr-0 sm:group-hover/menu-sub-item:pr-10'
                  : ''
              }
            >
              {item.title}
            </span>
          </Link>
        </SidebarMenuSubButton>
        {item.quickActions && (
          <QuickActions
            quickActions={item.quickActions}
            groupClass="group-hover/menu-sub-item:opacity-100"
            positioning="default"
          />
        )}
      </SidebarMenuSubItem>
    );
  }

  if (isItemWithChildren(item)) {
    return (
      <Collapsible
        asChild
        defaultOpen={item.isActive?.() || hasActiveChild(item)}
        onOpenChange={item.onExpandChange}
      >
        <SidebarMenuSubItem key={item.title}>
          <SidebarMenuSubButton asChild isActive={item.isActive?.()} size="sm">
            <button
              onClick={item.onClick}
              className="cursor-pointer w-full group/collapsible-item relative has-[.chevron-wrapper:active]:!bg-sidebar-accent"
            >
              <CollapsibleTrigger
                asChild
                onClick={(e) => e.stopPropagation()}
                onPointerDownCapture={(e) => e.stopPropagation()}
              >
                <div className="flex w-4 h-4 group/trigger items-center justify-center chevron-wrapper">
                  <Icon
                    icon={item.icon || 'container'}
                    className="group-hover/collapsible-item:opacity-0 opacity-100 transition-opacity"
                  />
                  <div className="hover:bg-zinc-200 active:bg-zinc-300 rounded p-0.5 absolute">
                    <Icon
                      icon="chevron-right"
                      className="transition-all ease-in group-data-[state=open]/trigger:rotate-90 group-hover/collapsible-item:opacity-100 opacity-0 duration-75"
                    />
                  </div>
                  <span className="sr-only">Toggle</span>
                </div>
              </CollapsibleTrigger>
              <span
                className={
                  item.quickActions && item.quickActions.length > 0
                    ? 'pr-10 sm:pr-0 sm:group-hover/menu-sub-item:pr-10'
                    : ''
                }
              >
                {item.title}
              </span>
            </button>
          </SidebarMenuSubButton>
          {item.quickActions && (
            <QuickActions
              quickActions={item.quickActions}
              groupClass="group-hover/menu-sub-item:opacity-100"
              positioning="default"
            />
          )}
          <CollapsibleContent>
            <SidebarMenuSub>
              {item.items.map((subItem) => (
                <RecursiveNavItem
                  key={subItem.title}
                  item={subItem}
                  depth={depth + 1}
                />
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuSubItem>
      </Collapsible>
    );
  }

  // Simple item without children
  return (
    <SidebarMenuSubItem key={item.title} ref={scrollRef}>
      <SidebarMenuSubButton asChild isActive={item.isActive?.()} size="sm">
        <button onClick={item.onClick} className="cursor-pointer w-full">
          <Icon icon={item.icon || 'container'} />
          <span
            className={
              item.quickActions && item.quickActions.length > 0
                ? 'pr-10 sm:pr-0 sm:group-hover/menu-sub-item:pr-10'
                : ''
            }
          >
            {item.title}
          </span>
        </button>
      </SidebarMenuSubButton>
      {item.quickActions && (
        <QuickActions
          quickActions={item.quickActions}
          groupClass="group-hover/menu-sub-item:opacity-100"
        />
      )}
    </SidebarMenuSubItem>
  );
};

export const CollapsibleItemNavItem = ({
  item,
}: CollapsibleItemNavItemProps) => {
  return (
    <Collapsible
      asChild
      defaultOpen={item.isActive?.() || hasActiveChild(item)}
      onOpenChange={item.onExpandChange}
    >
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          tooltip={item.title}
          isActive={item.isActive?.()}
        >
          <button
            onClick={item.onClick}
            className="cursor-pointer group/collapsible-item relative has-[.chevron-wrapper:active]:!bg-sidebar-accent"
          >
            <CollapsibleTrigger
              asChild
              onClick={(e) => e.stopPropagation()}
              onPointerDownCapture={(e) => e.stopPropagation()}
            >
              <div className="flex w-4 h-4 group/trigger items-center justify-center chevron-wrapper">
                <Icon
                  icon={item.icon!}
                  className="group-hover/collapsible-item:opacity-0 opacity-100 transition-opacity"
                />
                <div className="hover:bg-zinc-200 active:bg-zinc-300 rounded p-0.5 absolute">
                  <Icon
                    icon="chevron-right"
                    className="transition-all ease-in group-data-[state=open]/trigger:rotate-90 group-hover/collapsible-item:opacity-100 opacity-0 duration-75"
                  />
                </div>
                <span className="sr-only">Toggle</span>
              </div>
            </CollapsibleTrigger>
            <span
              className={
                item.quickActions && item.quickActions.length > 0
                  ? 'pr-10 sm:pr-0 sm:group-hover/menu-item:pr-10'
                  : ''
              }
            >
              {item.title}
            </span>
          </button>
        </SidebarMenuButton>
        {item.quickActions && (
          <QuickActions
            quickActions={item.quickActions}
            positioning="default"
          />
        )}
        <CollapsibleContent>
          <SidebarMenuSub>
            {item.items.map((subItem) => (
              <RecursiveNavItem key={subItem.title} item={subItem} />
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
};

type SidebarSectionProps = {
  section: SideBarSection;
};

export const SidebarSection = ({ section }: SidebarSectionProps) => {
  return (
    <SidebarGroup className="group/section">
      <SidebarGroupLabel>
        <span
          className={
            section.quickActions && section.quickActions.length > 0
              ? 'pr-10 sm:pr-0 sm:group-hover/section:pr-10'
              : ''
          }
        >
          {section.title}
        </span>
        {section.quickActions && (
          <QuickActions
            quickActions={section.quickActions}
            groupClass="group-hover/section:opacity-100"
            positioning="none"
          />
        )}
      </SidebarGroupLabel>
      <SidebarMenu>
        {section.items.map((item, itemIndex) => {
          if (isLinkItem(item)) {
            return <LinkNavItem key={itemIndex} item={item} />;
          }

          if (isItemWithChildren(item)) {
            return <CollapsibleItemNavItem key={itemIndex} item={item} />;
          }

          if (isSimpleItem(item)) {
            return <SimpleItemNavItem key={itemIndex} item={item} />;
          }

          return null;
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
};
